import { getThemes, getTokens } from "../config.js";
import { THEME_CLASSNAME_PREFIX, THEME_NAME_SEPARATOR } from "../constants/constants.js";
import { getThemeUnwrapped } from "../hooks/getThemeUnwrapped.js";
const emptyState = { name: "" };
function hasNoThemeUpdatingProps(props) {
  return !(props.name || props.componentName || props.inverse || props.reset);
}
class ThemeManager {
  constructor(props = {}, parentManager) {
    this.props = props;
    this.themeListeners = /* @__PURE__ */ new Set();
    this.parentManager = null;
    this.state = emptyState;
    this._allKeys = null;
    if (parentManager === "root") {
      this.updateState(props, false);
      return;
    }
    if (!parentManager) {
      if (process.env.NODE_ENV !== "production") {
        console.trace();
        throw new Error(`No parent manager given`);
      }
      throw `\u274C`;
    }
    this.themeListeners = parentManager.themeListeners;
    if (hasNoThemeUpdatingProps(props)) {
      return parentManager;
    }
    if (parentManager) {
      this.parentManager = parentManager;
    }
    const updatedState = this.getStateIfChanged(props);
    if (updatedState) {
      this.state = updatedState;
      return;
    }
    return parentManager || this;
  }
  updateState(props = this.props || {}, notify = true) {
    const shouldFlush = (() => {
      if (props.forceTheme) {
        this.state.theme = props.forceTheme;
        this.state.name = props.name || "";
        return true;
      }
      const nextState = this.getStateIfChanged(props);
      if (nextState) {
        this.state = nextState;
        return true;
      }
    })();
    if (shouldFlush) {
      this._allKeys = null;
      notify && this.notify();
      return this.state;
    }
  }
  getStateIfChanged(props = this.props, state = this.state, parentManager = this.parentManager) {
    const _ = this.getState(props, parentManager);
    if (state && state !== emptyState && !_) {
      return parentManager == null ? void 0 : parentManager.state;
    }
    if (this.getStateShouldChange(_, state)) {
      return _;
    }
  }
  getStateShouldChange(nextState, state = this.state) {
    if (!(nextState == null ? void 0 : nextState.theme) || nextState.theme === (state == null ? void 0 : state.theme)) {
      return false;
    }
    return true;
  }
  getState(props = this.props, parentManager = this.parentManager) {
    return getState(props, parentManager) || (process.env.TAMAGUI_TARGET === "native" ? (parentManager == null ? void 0 : parentManager.state) || null : null);
  }
  get allKeys() {
    var _a;
    this._allKeys ||= /* @__PURE__ */ new Set([
      ...((_a = this.parentManager) == null ? void 0 : _a.allKeys) || [],
      ...Object.keys(this.state.theme || {})
    ]);
    return this._allKeys;
  }
  // gets value going up to parents
  getValue(key, state) {
    if (!key)
      return;
    let theme = (state || this.state).theme;
    let manager = this;
    while (theme && manager) {
      if (key in theme) {
        return theme[key];
      }
      manager = manager.parentManager;
      theme = manager == null ? void 0 : manager.state.theme;
    }
    const tokens = getTokens();
    if (key in tokens.color) {
      return tokens.color[key];
    }
  }
  notify() {
    this.themeListeners.forEach((cb) => cb(this.state.name, this));
  }
  onChangeTheme(cb) {
    this.themeListeners.add(cb);
    return () => {
      this.themeListeners.delete(cb);
    };
  }
}
function getNextThemeClassName(name, isInverting = false) {
  const next = `t_sub_theme ${THEME_CLASSNAME_PREFIX}${name}`;
  if (isInverting) {
    return next + // ensure you invert to base dark as well as specific dark
    // ... logic should likely be elsewhere
    (next.includes("dark_") ? ` t_dark` : next.includes("light_") ? ` t_light` : "");
  }
  return next.replace("light_", "").replace("dark_", "");
}
const cache = /* @__PURE__ */ new WeakMap();
function getState(props, parentManager) {
  var _a, _b, _c;
  const cached = cache.get(props);
  if (cached && cached[0] === parentManager) {
    return cached[1];
  }
  const themes = getThemes();
  if (props.name && props.reset) {
    throw new Error("Cannot reset + set new name");
  }
  if (props.reset && !(parentManager == null ? void 0 : parentManager.parentManager)) {
    console.warn("Cannot reset no grandparent exists");
    return null;
  }
  let result = null;
  const nextName = props.reset ? ((_b = (_a = parentManager == null ? void 0 : parentManager.parentManager) == null ? void 0 : _a.state) == null ? void 0 : _b.name) || "" : props.name || "";
  const { componentName } = props;
  const parentName = ((_c = parentManager == null ? void 0 : parentManager.state) == null ? void 0 : _c.name) || "";
  const base = parentName.split(THEME_NAME_SEPARATOR);
  const lastSegment = base[base.length - 1];
  const isParentAComponentTheme = parentName && lastSegment[0].toUpperCase() === lastSegment[0];
  if (isParentAComponentTheme) {
    base.pop();
  }
  const parentBaseTheme = isParentAComponentTheme ? base.slice(0, base.length).join(THEME_NAME_SEPARATOR) : parentName;
  const max = base.length;
  const min = componentName && !nextName ? max : 0;
  if (process.env.NODE_ENV === "development" && props.debug === "verbose")
    [console.groupCollapsed("ThemeManager.getState()", props, { parentName, parentBaseTheme, base, min, max, isParentAComponentTheme }), console.trace(), console.groupEnd()];
  for (let i = max; i >= min; i--) {
    let prefix = base.slice(0, i).join(THEME_NAME_SEPARATOR);
    if (props.inverse) {
      prefix = inverseThemeName(prefix);
    }
    const potentials = [];
    if (prefix && prefix !== parentBaseTheme) {
      potentials.push(prefix);
    }
    if (nextName) {
      potentials.unshift(prefix ? `${prefix}_${nextName}` : nextName);
    }
    if (i === 1) {
      const lastSegment2 = potentials.findIndex((x) => !x.includes("_"));
      if (lastSegment2 > 0) {
        potentials.splice(lastSegment2, 0, nextName);
      }
    }
    if (componentName) {
      if (nextName) {
        potentials.push(
          `${prefix.slice(
            0,
            prefix.indexOf(THEME_NAME_SEPARATOR)
          )}_${nextName}_${componentName}`
        );
      }
      potentials.push(`${prefix}_${componentName}`);
      if (nextName) {
        potentials.unshift(`${prefix}_${nextName}_${componentName}`);
      }
    }
    const found = potentials.find((t) => t in themes);
    if (process.env.NODE_ENV === "development" && props.debug === "verbose")
      console.log("getState found", found, "from", potentials, "parentName", parentName);
    if (found) {
      if (found === parentName) {
        break;
      }
      result = {
        name: found,
        theme: getThemeUnwrapped(themes[found]),
        className: getNextThemeClassName(found, props.inverse),
        parentName
      };
      break;
    }
  }
  if (parentManager) {
    cache.set(props, [parentManager, result]);
  }
  return result;
}
const inverseThemeName = (themeName) => {
  return themeName.startsWith("light") ? themeName.replace(/^light/, "dark") : themeName.replace(/^dark/, "light");
};
export {
  ThemeManager,
  hasNoThemeUpdatingProps
};
//# sourceMappingURL=ThemeManager.js.map
